import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { compressDocument, compressSignature, formatFileSize } from '../utils/imageCompressor';

const LoanManagement = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, active, completed, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLoan, setSelectedLoan] = useState(null);
  // showDetailModal removed - now using full page detail view (/loan/:id)
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Approval Form Data
  const [approvalData, setApprovalData] = useState({
    remarks: '',
    paymentMethod: 'cash',
    transactionId: '',
    proofFile: null,
    preferredPaymentDate: 1 // EMI date (1-31), default to 1st
  });

  // Proof Upload Data (for after approval)
  const [proofData, setProofData] = useState({
    paymentMethod: 'cash',
    transactionId: '',
    proofFile: null
  });

  // Rejection Form Data
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    remarks: '',
    reapplyAfter: 6 // Default 6 months
  });

  // Document Upload Data (Low Documentation - Only Payment Proof and Account Details)
  const [documentData, setDocumentData] = useState({
    type: 'payment_proof',
    file: null,
    description: ''
  });
  
  // Signature Upload Data
  const [signatureData, setSignatureData] = useState({
    file: null
  });
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  useEffect(() => {
    fetchLoans();
  }, []);

  // Filter and search effects
  useEffect(() => {
    let filtered = loans;

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(loan => loan.status === filter);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(loan => {
        const customerName = loan.customer?.name || loan.name || '';
        const customerPhone = loan.customer?.phone || loan.phone || '';
        const loanType = loan.loanType || '';
        const loanId = loan._id || '';
        return (
          customerName.toLowerCase().includes(searchLower) ||
          customerPhone.includes(searchTerm) ||
          loanType.toLowerCase().includes(searchLower) ||
          loanId.toString().includes(searchTerm)
        );
      });
    }

    setFilteredLoans(filtered);
  }, [loans, filter, searchTerm]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${config.API_URL}/enhanced-loans/`);
      
      let loansData = [];
      if (Array.isArray(response.data)) {
        loansData = response.data;
      } else if (response.data.success && response.data.loans) {
        loansData = response.data.loans;
      } else if (response.data.success && response.data.data) {
        loansData = response.data.data;
      }
      
      // Sort by creation date (newest first)
      loansData.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.startDate || 0);
        const dateB = new Date(b.createdAt || b.startDate || 0);
        return dateB - dateA;
      });
      
      setLoans(loansData);
    } catch (error) {
      console.error('Error fetching loans:', error);
      setError('Failed to fetch loans. Please try again.');
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (loan) => {
    // Navigate to full page detail view instead of opening modal
    navigate(`/loan/${loan._id}`);
  };

  const handleApproveClick = (loan) => {
    setSelectedLoan(loan);
    setApprovalData({
      remarks: '',
      paymentMethod: 'cash',
      transactionId: '',
      proofFile: null
    });
    setShowApprovalModal(true);
  };

  const handleRejectClick = (loan) => {
    setSelectedLoan(loan);
    setRejectionData({
      reason: '',
      remarks: '',
      reapplyAfter: 6 // Default 6 months
    });
    setShowRejectModal(true);
  };

  const handleApproval = async () => {
    try {
      setProcessing(true);
      
      const formData = new FormData();
      formData.append('action', 'approve');
      formData.append('remarks', approvalData.remarks || 'Approved by admin');
      formData.append('paymentMethod', approvalData.paymentMethod);
      formData.append('transactionId', approvalData.transactionId || '');
      formData.append('preferredPaymentDate', approvalData.preferredPaymentDate || 1);

      // Proof is optional - can be uploaded later
      if (approvalData.proofFile) {
        formData.append('proof', approvalData.proofFile);
      }

      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/admin-action`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success || response.data.message) {
        alert(response.data.message || 'Loan approved successfully!');
        setShowApprovalModal(false);
        // Modal removed - using full page view
        await fetchLoans();
        setSelectedLoan(null);
      }
    } catch (error) {
      console.error('Error approving loan:', error);
      const errorMsg = error.response?.data?.message || 'Failed to approve loan. Please try again.';
      alert(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejection = async () => {
    if (!rejectionData.reason || !rejectionData.reason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      
      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/admin-action`,
        {
          action: 'reject',
          remarks: `${rejectionData.reason}${rejectionData.remarks ? '\n\nAdditional Notes: ' + rejectionData.remarks : ''}`,
          reapplyAfter: rejectionData.reapplyAfter || 6 // Months after which user can reapply
        }
      );
      
      if (response.data.success || response.data.message) {
        alert(response.data.message || 'Loan rejected successfully!');
        setShowRejectModal(false);
        // Modal removed - using full page view
        await fetchLoans();
        setSelectedLoan(null);
      }
    } catch (error) {
      console.error('Error rejecting loan:', error);
      const errorMsg = error.response?.data?.message || 'Failed to reject loan. Please try again.';
      alert(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!documentData.file) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setProcessing(true);
      
      // Compress image before upload
      let fileToUpload = documentData.file;
      if (documentData.file.type.startsWith('image/')) {
        console.log('📦 Compressing image before upload...');
        if (documentData.type === 'customer_signature') {
          fileToUpload = await compressSignature(documentData.file);
        } else {
          fileToUpload = await compressDocument(documentData.file);
        }
      }
      
      const formData = new FormData();
      formData.append('documentType', documentData.type);
      formData.append('description', documentData.description || '');
      formData.append('file', fileToUpload);

      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/upload-document`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        alert('Document uploaded successfully!');
        setShowDocumentModal(false);
        setDocumentData({
          type: 'payment_proof',
          file: null,
          description: ''
        });
        // Refresh loan details
        await handleViewDetails(selectedLoan);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleUploadProof = async () => {
    if (!proofData.proofFile) {
      alert('Please select a proof file to upload');
      return;
    }

    try {
      setProcessing(true);
      
      const formData = new FormData();
      formData.append('paymentMethod', proofData.paymentMethod);
      formData.append('transactionId', proofData.transactionId || '');
      formData.append('proof', proofData.proofFile);

      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/upload-proof`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        alert('Payment proof uploaded successfully!');
        setShowProofModal(false);
        setProofData({
          paymentMethod: 'cash',
          transactionId: '',
          proofFile: null
        });
        // Refresh loan details
        await handleViewDetails(selectedLoan);
      }
    } catch (error) {
      console.error('Error uploading proof:', error);
      alert('Failed to upload proof: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleProofClick = (loan) => {
    setSelectedLoan(loan);
    setProofData({
      paymentMethod: loan.disbursementProof?.paymentMethod || 'cash',
      transactionId: loan.disbursementProof?.transactionId || '',
      proofFile: null
    });
    setShowProofModal(true);
  };

  const handleDocumentClick = (loan) => {
    setSelectedLoan(loan);
    setDocumentData({
      type: 'payment_proof',
      file: null,
      description: ''
    });
    setShowDocumentModal(true);
  };

  const handleUploadSignature = async () => {
    if (!signatureData.file) {
      alert('Please select a signature file to upload');
      return;
    }

    try {
      setProcessing(true);
      
      const formData = new FormData();
      formData.append('signature', signatureData.file);

      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/upload-signature`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        alert('Signature uploaded successfully!');
        setShowSignatureModal(false);
        setSignatureData({ file: null });
        // Refresh loan details
        await handleViewDetails(selectedLoan);
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      alert('Failed to upload signature: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleSignatureClick = (loan) => {
    setSelectedLoan(loan);
    setSignatureData({ file: null });
    setShowSignatureModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending Review', icon: 'bi-clock' },
      active: { class: 'bg-success', text: 'Active', icon: 'bi-check-circle' },
      completed: { class: 'bg-info', text: 'Completed', icon: 'bi-check-circle-fill' },
      rejected: { class: 'bg-danger', text: 'Rejected', icon: 'bi-x-circle' },
      defaulted: { class: 'bg-dark', text: 'Defaulted', icon: 'bi-exclamation-triangle' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`badge ${config.class} d-flex align-items-center gap-1`} style={{ width: 'fit-content' }}>
        <i className={`bi ${config.icon}`}></i>
        {config.text}
      </span>
    );
  };

  const getRiskBadge = (riskLevel) => {
    const riskConfig = {
      low: { class: 'bg-success', text: 'Low Risk' },
      medium: { class: 'bg-warning text-dark', text: 'Medium Risk' },
      high: { class: 'bg-danger', text: 'High Risk' },
      very_high: { class: 'bg-dark', text: 'Very High Risk' }
    };

    const config = riskConfig[riskLevel] || riskConfig.medium;
    
    return (
      <span className={`badge ${config.class}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStats = () => {
    const total = loans.length;
    const pending = loans.filter(l => l.status === 'pending').length;
    const active = loans.filter(l => l.status === 'active').length;
    const completed = loans.filter(l => l.status === 'completed').length;
    const rejected = loans.filter(l => l.status === 'rejected').length;
    
    return { total, pending, active, completed, rejected };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading loan applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h5 className="alert-heading">Error!</h5>
          <p>{error}</p>
          <button className="btn btn-danger" onClick={fetchLoans}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0 d-flex align-items-center">
            <i className="bi bi-bank me-2 fs-2 text-primary"></i>
            Loan Management
          </h2>
          <p className="text-muted mb-0">Manage loan applications, approvals, and documents</p>
        </div>
        <div>
          <button className="btn btn-outline-primary" onClick={fetchLoans}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card border-primary">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Applications</h6>
              <h3 className="mb-0">{stats.total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card border-warning">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Pending Review</h6>
              <h3 className="mb-0 text-warning">{stats.pending}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card border-success">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Active Loans</h6>
              <h3 className="mb-0 text-success">{stats.active}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card border-info">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Completed</h6>
              <h3 className="mb-0 text-info">{stats.completed}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Search Loans</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by customer name, phone, or loan ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label">Filter by Status</label>
              <div className="btn-group w-100" role="group">
                <button
                  type="button"
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All ({stats.total})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('pending')}
                >
                  Pending ({stats.pending})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setFilter('active')}
                >
                  Active ({stats.active})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'completed' ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => setFilter('completed')}
                >
                  Completed ({stats.completed})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'rejected' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setFilter('rejected')}
                >
                  Rejected ({stats.rejected})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Application ID</th>
                  <th>Customer</th>
                  <th>Loan Details</th>
                  <th>Amount</th>
                  <th>Risk Level</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLoans.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">
                        {searchTerm || filter !== 'all' 
                          ? 'No loans found matching your criteria'
                          : 'No loan applications found'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredLoans.map((loan) => (
                    <tr key={loan._id}>
                      <td>
                        <small className="text-muted">APP-</small>
                        <strong>{loan._id.toString().slice(-6).toUpperCase()}</strong>
                      </td>
                      <td>
                        <strong>{loan.customer?.name || loan.name || 'N/A'}</strong><br />
                        <small className="text-muted">{loan.customer?.phone || loan.phone || 'N/A'}</small>
                      </td>
                      <td>
                        <span className="badge bg-primary">{loan.loanType || 'Personal Loan'}</span><br />
                        <small className="text-muted">{loan.loanPurpose || 'N/A'}</small>
                      </td>
                      <td>
                        <strong>{formatCurrency(loan.loanAmount)}</strong><br />
                        <small className="text-muted">
                          {loan.tenure} months @ {loan.interestRate || 24}% p.a.
                        </small>
                      </td>
                      <td>{getRiskBadge(loan.riskLevel)}</td>
                      <td>{getStatusBadge(loan.status)}</td>
                      <td>
                        <small>{formatDate(loan.createdAt || loan.startDate)}</small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleViewDetails(loan)}
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i> View Details
                          </button>
                          {loan.status === 'active' && (
                            <button
                              className="btn btn-outline-info"
                              onClick={() => handleDocumentClick(loan)}
                              title="Manage Documents"
                            >
                              <i className="bi bi-file-earmark-text"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Loan Detail Modal - REMOVED: Now using full page detail view (/loan/:id) */}
      {false && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Loan Application Details
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => {
                    // Modal removed - using full page view
                    setSelectedLoan(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Customer Information */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0"><i className="bi bi-person me-2"></i>Customer Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Name:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'}</p>
                        <p><strong>Phone:</strong> {selectedLoan.customer?.phone || selectedLoan.phone || 'N/A'}</p>
                        <p><strong>Address:</strong> {selectedLoan.customer?.address || selectedLoan.address || 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Credit Score:</strong> {selectedLoan.creditScore?.creditScore || 'N/A'}</p>
                        <p><strong>Credit Grade:</strong> 
                          <span className={`badge ms-2 ${
                            selectedLoan.creditScore?.creditGrade?.includes('A') ? 'bg-success' :
                            selectedLoan.creditScore?.creditGrade?.includes('B') ? 'bg-info' :
                            selectedLoan.creditScore?.creditGrade?.includes('C') ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {selectedLoan.creditScore?.creditGrade || 'N/A'}
                          </span>
                        </p>
                      </div>
                    </div>
                    {/* Location Information */}
                    {selectedLoan.location && selectedLoan.location.latitude && selectedLoan.location.longitude && (
                      <div className="alert alert-info mt-3 mb-0">
                        <h6 className="alert-heading">
                          <i className="bi bi-geo-alt me-2"></i>Location Verification
                        </h6>
                        <hr />
                        <p className="mb-2"><strong>Address:</strong> {selectedLoan.location.address || selectedLoan.customer?.address || 'N/A'}</p>
                        <p className="mb-2"><strong>Coordinates:</strong> {selectedLoan.location.latitude.toFixed(6)}, {selectedLoan.location.longitude.toFixed(6)}</p>
                        <a
                          href={`https://www.google.com/maps?q=${selectedLoan.location.latitude},${selectedLoan.location.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="bi bi-map me-1"></i>
                          View on Google Maps
                        </a>
                        <small className="d-block text-muted mt-2">
                          <i className="bi bi-info-circle me-1"></i>
                          Verify this location before approving the loan. Click "View on Google Maps" to see exact location.
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Loan Information */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0"><i className="bi bi-bank me-2"></i>Loan Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Loan Type:</strong> {selectedLoan.loanType || 'N/A'}</p>
                        <p><strong>Purpose:</strong> {selectedLoan.loanPurpose || selectedLoan.purpose || 'N/A'}</p>
                        <p><strong>Requested Amount:</strong> {formatCurrency(selectedLoan.loanAmount)}</p>
                        <p><strong>Tenure:</strong> {selectedLoan.tenure || 'N/A'} months</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Interest Rate:</strong> {selectedLoan.interestRate || 'N/A'}% p.a.</p>
                        <p><strong>Total Amount:</strong> {formatCurrency(selectedLoan.totalAmount)}</p>
                        <p><strong>Monthly EMI:</strong> {formatCurrency(selectedLoan.monthlyInstallment)}</p>
                        <p><strong>Risk Level:</strong> {getRiskBadge(selectedLoan.riskLevel)}</p>
                      </div>
                    </div>
                    {/* Processing Fee Section - Admin Only */}
                    {selectedLoan.status === 'active' && (selectedLoan.processingFee > 0 || selectedLoan.actualDisbursedAmount > 0) && (
                      <div className="alert alert-info mt-3 mb-0">
                        <h6 className="alert-heading">
                          <i className="bi bi-calculator me-2"></i>Financial Breakdown (Admin View Only)
                        </h6>
                        <hr />
                        <div className="row">
                          <div className="col-md-4">
                            <small className="text-muted d-block">Requested Amount:</small>
                            <strong>{formatCurrency(selectedLoan.loanAmount)}</strong>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted d-block">Processing Fee (5%):</small>
                            <strong className="text-warning">- {formatCurrency(selectedLoan.processingFee || Math.round(selectedLoan.loanAmount * 0.05 * 100) / 100)}</strong>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted d-block">Actual Disbursed:</small>
                            <strong className="text-success">{formatCurrency(selectedLoan.actualDisbursedAmount || (selectedLoan.loanAmount - (selectedLoan.processingFee || Math.round(selectedLoan.loanAmount * 0.05 * 100) / 100)))}</strong>
                          </div>
                        </div>
                        <hr className="my-2" />
                        <div className="row">
                          <div className="col-md-12">
                            <small className="text-muted d-block">Profit from Processing Fee:</small>
                            <strong className="text-success fs-6">{formatCurrency(selectedLoan.processingFee || Math.round(selectedLoan.loanAmount * 0.05 * 100) / 100)}</strong>
                            <small className="text-muted ms-2">(This will be included in profit dashboard)</small>
                          </div>
                        </div>
                        <small className="text-muted mt-2 d-block">
                          <i className="bi bi-info-circle me-1"></i>
                          Customer sees ₹{selectedLoan.loanAmount?.toLocaleString()} as disbursed amount. Actual disbursed is ₹{(selectedLoan.actualDisbursedAmount || (selectedLoan.loanAmount - (selectedLoan.processingFee || Math.round(selectedLoan.loanAmount * 0.05 * 100) / 100)))?.toLocaleString()}.
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Information */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0"><i className="bi bi-info-square me-2"></i>Status Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Status:</strong> {getStatusBadge(selectedLoan.status)}</p>
                        <p><strong>Application Date:</strong> {formatDate(selectedLoan.createdAt || selectedLoan.startDate)}</p>
                        {selectedLoan.approvedDate && (
                          <p><strong>Approved Date:</strong> {formatDate(selectedLoan.approvedDate)}</p>
                        )}
                        {selectedLoan.disbursementDate && (
                          <p><strong>Disbursement Date:</strong> {formatDate(selectedLoan.disbursementDate)}</p>
                        )}
                      </div>
                      <div className="col-md-6">
                        {selectedLoan.approvedBy && (
                          <p><strong>Approved By:</strong> {selectedLoan.approvedBy}</p>
                        )}
                        {selectedLoan.approvalNotes && (
                          <p><strong>Approval Notes:</strong> {selectedLoan.approvalNotes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Disbursement Proof */}
                <div className="card mb-3">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0"><i className="bi bi-file-earmark-check me-2"></i>Disbursement Proof</h6>
                    {(selectedLoan.status === 'active' || selectedLoan.status === 'completed') && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleProofClick(selectedLoan)}
                      >
                        <i className="bi bi-upload me-1"></i>
                        {selectedLoan.disbursementProof ? 'Update Proof' : 'Upload Proof'}
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {selectedLoan.disbursementProof ? (
                      <>
                        <p><strong>Payment Method:</strong> {selectedLoan.disbursementProof.paymentMethod || 'N/A'}</p>
                        {selectedLoan.disbursementProof.transactionId && (
                          <p><strong>Transaction ID:</strong> {selectedLoan.disbursementProof.transactionId}</p>
                        )}
                        {selectedLoan.disbursementProof.path && (
                          <p>
                            <strong>Proof File:</strong> 
                            <a 
                              href={`/uploads/${selectedLoan.disbursementProof.path.replace(/^uploads\//, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary ms-2"
                            >
                              <i className="bi bi-eye me-1"></i>View Proof
                            </a>
                          </p>
                        )}
                        <p><strong>Uploaded:</strong> {formatDate(selectedLoan.disbursementProof.uploadedAt)}</p>
                      </>
                    ) : (
                      <div className="alert alert-warning mb-0">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No payment proof uploaded yet. Click "Upload Proof" to add proof.
                      </div>
                    )}
                  </div>
                </div>

                {/* Auto-generated Loan Agreement */}
                {selectedLoan.loanAgreement && (
                  <div className="card mb-3">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0"><i className="bi bi-file-earmark-pdf me-2"></i>Loan Agreement</h6>
                    </div>
                    <div className="card-body">
                      <p><strong>Agreement Number:</strong> {selectedLoan.loanAgreement.agreementNumber}</p>
                      {selectedLoan.loanAgreement.generatedAt && (
                        <p><strong>Generated:</strong> {formatDate(selectedLoan.loanAgreement.generatedAt)}</p>
                      )}
                      {(selectedLoan.loanAgreement.path || selectedLoan.loanAgreement.url) && (
                        <a 
                          href={selectedLoan.loanAgreement.url || `/uploads/${selectedLoan.loanAgreement.path.replace(/^uploads\//, '')}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-download me-1"></i>Download Agreement
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents Section (Low Documentation) */}
                <div className="card mb-3">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0"><i className="bi bi-file-earmark-text me-2"></i>Loan Documents</h6>
                    {(selectedLoan.status === 'active' || selectedLoan.status === 'completed') && (
                      <div>
                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleDocumentClick(selectedLoan)}
                        >
                          <i className="bi bi-plus-circle me-1"></i>Add Document
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleSignatureClick(selectedLoan)}
                          title="Upload Customer Signature"
                        >
                          <i className="bi bi-pen me-1"></i>Upload Signature
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="card-body">
                    {/* Customer Signature */}
                    {selectedLoan.customerSignature && (
                      <div className="alert alert-success mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="bi bi-check-circle me-2"></i>
                            <strong>Customer Signature:</strong> Uploaded on {formatDate(selectedLoan.customerSignature.uploadedAt)}
                          </div>
                          <a 
                            href={`/uploads/${selectedLoan.customerSignature.path.replace(/^uploads\//, '')}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-success"
                          >
                            <i className="bi bi-eye me-1"></i>View
                          </a>
                        </div>
                      </div>
                    )}

                    {selectedLoan.documents && selectedLoan.documents.length > 0 ? (
                      <div className="list-group">
                        {selectedLoan.documents.map((doc, idx) => (
                          <div key={idx} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>
                                  <i className="bi bi-file-earmark me-2"></i>
                                  {doc.type === 'payment_proof' ? 'Payment Proof (Disbursement Slip)' :
                                   doc.type === 'account_details' ? 'Account Details / Checkbook' :
                                   doc.type === 'loan_agreement' ? 'Loan Agreement (Auto-generated)' :
                                   doc.type === 'signature' ? 'Customer Signature' :
                                   'Document'}
                                </strong>
                                {doc.description && (
                                  <div className="small text-muted mt-1">{doc.description}</div>
                                )}
                                {doc.uploadedAt && (
                                  <div className="small text-muted">
                                    <i className="bi bi-calendar me-1"></i>
                                    {formatDate(doc.uploadedAt)}
                                  </div>
                                )}
                              </div>
                              {(doc.path || doc.url) && (
                                <a 
                                  href={doc.url || `/uploads/${doc.path.replace(/^uploads\//, '')}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-eye me-1"></i>View
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="alert alert-info mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Low Documentation System:</strong> Only Payment Proof (disbursement slip) and Account Details/Checkbook images are required. Customer verification documents (Photo, Aadhar, PAN) are already available from verification.
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Schedule */}
                {selectedLoan.payments && selectedLoan.payments.length > 0 && (
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <h6 className="mb-0"><i className="bi bi-calendar3 me-2"></i>Payment Schedule</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Due Date</th>
                              <th>Amount</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLoan.payments.map((payment, idx) => (
                              <tr key={idx}>
                                <td>{payment.installmentNumber || idx + 1}</td>
                                <td>{formatDate(payment.dueDate)}</td>
                                <td>{formatCurrency(payment.amount)}</td>
                                <td>
                                  {payment.status === 'completed' && (
                                    <span className="badge bg-success">Paid</span>
                                  )}
                                  {payment.status === 'pending' && (
                                    <span className="badge bg-warning text-dark">Pending</span>
                                  )}
                                  {payment.status === 'overdue' && (
                                    <span className="badge bg-danger">Overdue</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Witness Information */}
                {selectedLoan.witness && (selectedLoan.witness.witnessName || selectedLoan.witness.witnessPhone) && (
                  <div className="card mb-3">
                    <div className="card-header bg-info text-white">
                      <h6 className="mb-0"><i className="bi bi-person-check me-2"></i>Witness Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-info mb-2">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Note:</strong> This loan has been issued on the witness of the person mentioned below (Just as a Formality).
                      </div>
                      <p><strong>Witness Name:</strong> {selectedLoan.witness.witnessName || 'N/A'}</p>
                      <p><strong>Witness Phone:</strong> {selectedLoan.witness.witnessPhone || 'N/A'}</p>
                    </div>
                  </div>
                )}

                {/* Navigation to EMI Dashboard */}
                {selectedLoan.status === 'active' && (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Active Loan:</strong> Payment management is available in the{' '}
                    <a href="/loan-emi-dashboard" className="alert-link">Loan EMI Dashboard</a>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {selectedLoan.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => {
                        // Modal removed - using full page view
                        handleApproveClick(selectedLoan);
                      }}
                    >
                      <i className="bi bi-check-circle me-1"></i> Approve
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        // Modal removed - using full page view
                        handleRejectClick(selectedLoan);
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i> Reject
                    </button>
                  </>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    // Modal removed - using full page view
                    setSelectedLoan(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-check-circle me-2"></i>
                  Approve Loan Application
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowApprovalModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Loan Details:</strong><br />
                  Customer: {selectedLoan.customer?.name || selectedLoan.name || 'N/A'}<br />
                  Amount: {formatCurrency(selectedLoan.loanAmount)}<br />
                  Tenure: {selectedLoan.tenure} months
                </div>

                <div className="mb-3">
                  <label className="form-label">Approval Remarks</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Add any remarks or notes about this approval..."
                    value={approvalData.remarks}
                    onChange={(e) => setApprovalData({...approvalData, remarks: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    value={approvalData.paymentMethod}
                    onChange={(e) => setApprovalData({...approvalData, paymentMethod: e.target.value})}
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Transaction ID (if applicable)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter transaction ID or reference number..."
                    value={approvalData.transactionId}
                    onChange={(e) => setApprovalData({...approvalData, transactionId: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>EMI Payment Date</strong> <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="31"
                    placeholder="Select EMI date (1-31)"
                    value={approvalData.preferredPaymentDate}
                    onChange={(e) => {
                      const date = parseInt(e.target.value);
                      if (date >= 1 && date <= 31) {
                        setApprovalData({...approvalData, preferredPaymentDate: date});
                      }
                    }}
                    required
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Select the date (1-31) when customer will pay EMI every month. This cannot be changed after disbursement.
                    <br />
                    <strong>Example:</strong> If customer wants to pay on 10th of every month, enter <strong>10</strong>
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Upload Payment Proof</strong> <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setApprovalData({...approvalData, proofFile: e.target.files[0]});
                      }
                    }}
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    You can upload payment proof now or add it later after disbursement. (Image or PDF, Max 5MB)
                  </small>
                  {approvalData.proofFile && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        {approvalData.proofFile.name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Note:</strong> Payment proof is optional during approval. You can upload it later after transferring the amount to customer's account.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowApprovalModal(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleApproval}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-1"></i>
                      Approve Loan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-x-circle me-2"></i>
                  Reject Loan Application
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowRejectModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Loan Details:</strong><br />
                  Customer: {selectedLoan.customer?.name || selectedLoan.name || 'N/A'}<br />
                  Amount: {formatCurrency(selectedLoan.loanAmount)}<br />
                  Tenure: {selectedLoan.tenure} months
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Rejection Reason</strong> <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={rejectionData.reason}
                    onChange={(e) => setRejectionData({...rejectionData, reason: e.target.value})}
                    required
                  >
                    <option value="">Select a reason...</option>
                    <option value="Insufficient Credit Score">Insufficient Credit Score</option>
                    <option value="Incomplete Documentation">Incomplete Documentation</option>
                    <option value="High Risk Profile">High Risk Profile</option>
                    <option value="Invalid KYC">Invalid KYC</option>
                    <option value="Customer Request">Customer Request</option>
                    <option value="Policy Violation">Policy Violation</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Additional Notes</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Provide additional details about the rejection..."
                    value={rejectionData.remarks}
                    onChange={(e) => setRejectionData({...rejectionData, remarks: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Reapply After (Months)</strong>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="24"
                    value={rejectionData.reapplyAfter}
                    onChange={(e) => setRejectionData({...rejectionData, reapplyAfter: parseInt(e.target.value) || 6})}
                    placeholder="6"
                  />
                  <small className="form-text text-muted">
                    This will be shown in the WhatsApp message. User can reapply anytime (no restriction enforced).
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleRejection}
                  disabled={processing || !rejectionData.reason}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-1"></i>
                      Reject Loan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Manage Loan Documents
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowDocumentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Loan:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'} - {formatCurrency(selectedLoan.loanAmount)}
                </div>

                {/* Existing Documents */}
                {selectedLoan.documents && selectedLoan.documents.length > 0 && (
                  <div className="mb-4">
                    <h6>Existing Documents</h6>
                    <div className="list-group">
                      {selectedLoan.documents.map((doc, idx) => (
                        <div key={idx} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{doc.type || doc.name || 'Document'}</strong>
                              {doc.uploadedAt && (
                                <small className="text-muted d-block">
                                  Uploaded: {formatDate(doc.uploadedAt)}
                                </small>
                              )}
                            </div>
                            {doc.url && (
                              <a 
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="bi bi-download me-1"></i>Download
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload New Document */}
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Document Type</strong> <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={documentData.type}
                    onChange={(e) => setDocumentData({...documentData, type: e.target.value})}
                    required
                  >
                    <option value="payment_proof">Payment Proof (Disbursement Slip)</option>
                    <option value="account_details">Account Details / Checkbook Image</option>
                    <option value="customer_signature">Customer Signature</option>
                  </select>
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Low Documentation: Customer verification documents (Photo, Aadhar, PAN) are already available from verification process.
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Document Description</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Brief description of the document..."
                    value={documentData.description}
                    onChange={(e) => setDocumentData({...documentData, description: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Upload Document</strong> <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setDocumentData({...documentData, file: e.target.files[0]});
                      }
                    }}
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Supported formats: Images, PDF, Word documents. Images will be auto-compressed 📦
                  </small>
                  {documentData.file && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        {documentData.file.name} ({formatFileSize(documentData.file.size)})
                      </span>
                      {documentData.file.type.startsWith('image/') && (
                        <span className="badge bg-info ms-2">
                          <i className="bi bi-arrow-down-circle me-1"></i>
                          Will be compressed before upload
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDocumentModal(false)}
                  disabled={processing}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUploadDocument}
                  disabled={processing || !documentData.file}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Upload Document
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofModal && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-upload me-2"></i>
                  Upload Payment Proof
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowProofModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Loan:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'} - {formatCurrency(selectedLoan.loanAmount)}
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    value={proofData.paymentMethod}
                    onChange={(e) => setProofData({...proofData, paymentMethod: e.target.value})}
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Transaction ID (if applicable)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter transaction ID or reference number..."
                    value={proofData.transactionId}
                    onChange={(e) => setProofData({...proofData, transactionId: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Upload Payment Proof</strong> <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setProofData({...proofData, proofFile: e.target.files[0]});
                      }
                    }}
                    required
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Upload payment receipt, screenshot, or proof document (Image or PDF, Max 5MB)
                  </small>
                  {proofData.proofFile && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        {proofData.proofFile.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowProofModal(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleUploadProof}
                  disabled={processing || !proofData.proofFile}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Upload Proof
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature Upload Modal */}
      {showSignatureModal && selectedLoan && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-pen me-2"></i>
                  Upload Customer Signature
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowSignatureModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Loan:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'} - {formatCurrency(selectedLoan.loanAmount)}
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Upload Customer Signature</strong> <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSignatureData({ file: e.target.files[0] });
                      }
                    }}
                    required
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Upload customer's digital signature or signed document (Image or PDF, Max 5MB)
                  </small>
                  {signatureData.file && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        {signatureData.file.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSignatureModal(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUploadSignature}
                  disabled={processing || !signatureData.file}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Upload Signature
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrops */}
      {(showApprovalModal || showRejectModal || showDocumentModal || showProofModal || showSignatureModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default LoanManagement;
